// apps/hr/src/pages/hr/Attendance.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Search,
  Filter,
  Download,
  RefreshCw,
  MoreHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

// ─── Helpers ──────────────────────────────────────────────────────
const statusConfig = {
  present: { label: 'Present', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  absent: { label: 'Absent', color: 'bg-red-100 text-red-700', icon: XCircle },
  leave: { label: 'On Leave', color: 'bg-amber-100 text-amber-700', icon: Calendar },
  half_day: { label: 'Half Day', color: 'bg-blue-100 text-blue-700', icon: Clock },
};

const statusOptions = Object.keys(statusConfig);

const Attendance = () => {
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // ─── Fetch employees ──────────────────────────────────────────
  const fetchEmployees = async () => {
    try {
      const records = await pb.collection('users').getFullList({
        filter: 'role != "user" && role != "admin" && role != "super_admin"',
        sort: 'name',
        requestKey: null,
      });
      setEmployees(records);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
      throw error;
    }
  };

  // ─── Fetch attendance for selected date ─────────────────────
  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const filter = `date = "${selectedDate}"`;
      const records = await pb.collection('attendance').getFullList({
        filter,
        requestKey: null,
      });
      setAttendance(records);
    } catch (error) {
      if (error.isAbort) {
        console.log('Request auto-cancelled (this is normal).');
        return;
      }
      console.error('Error fetching attendance:', error);
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  };

  // ─── Initial data load ──────────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      await fetchEmployees();
      await fetchAttendance();
    };
    loadData();
  }, []);

  // ─── Reload on date change ──────────────────────────────────
  useEffect(() => {
    if (employees.length > 0) {
      fetchAttendance();
    }
  }, [selectedDate]);

  // ─── Mark attendance ────────────────────────────────────────
  const markAttendance = async (employeeId, status) => {
    try {
      const existing = attendance.find((a) => a.employee === employeeId);
      const now = new Date().toLocaleTimeString();

      if (existing) {
        await pb.collection('attendance').update(existing.id, {
          status,
          checkIn: status === 'present' || status === 'half_day' ? now : '',
          checkOut: '',
        });
      } else {
        await pb.collection('attendance').create({
          employee: employeeId,
          date: selectedDate,
          status,
          checkIn: status === 'present' || status === 'half_day' ? now : '',
          checkOut: '',
        });
      }
      toast.success(`Marked as ${statusConfig[status].label}`);
      await fetchAttendance();
    } catch (error) {
      console.error('Error marking attendance:', error);
      toast.error('Failed to mark attendance');
    }
  };

  // ─── Quick mark all as present ─────────────────────────────
  const markAllPresent = async () => {
    if (!window.confirm('Mark all employees as present for today?')) return;
    try {
      for (const emp of employees) {
        const existing = attendance.find((a) => a.employee === emp.id);
        if (!existing || existing.status !== 'present') {
          await markAttendance(emp.id, 'present');
        }
      }
      toast.success('All employees marked present');
      await fetchAttendance();
    } catch (error) {
      toast.error('Failed to mark all');
    }
  };

  // ─── Handle refresh ──────────────────────────────────────────
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchEmployees();
    await fetchAttendance();
    setRefreshing(false);
    toast.success('Refreshed');
  };

  // ─── Filtered employees ──────────────────────────────────────
  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch = emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          emp.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const attendanceRecord = attendance.find((a) => a.employee === emp.id);
    const matchesStatus = filterStatus === 'all' || (attendanceRecord?.status === filterStatus);
    return matchesSearch && matchesStatus;
  });

  // ─── Stats ──────────────────────────────────────────────────
  const totalPresent = employees.filter((emp) => {
    const rec = attendance.find((a) => a.employee === emp.id);
    return rec?.status === 'present';
  }).length;

  const totalAbsent = employees.filter((emp) => {
    const rec = attendance.find((a) => a.employee === emp.id);
    return rec?.status === 'absent';
  }).length;

  const totalLeave = employees.filter((emp) => {
    const rec = attendance.find((a) => a.employee === emp.id);
    return rec?.status === 'leave';
  }).length;

  const totalNotMarked = employees.length - (totalPresent + totalAbsent + totalLeave);

  return (
    <div className="p-6">
      {/* ─── Header ─────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-indigo-600" />
            Attendance
          </h1>
          <p className="text-sm text-slate-500">Track daily employee attendance</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={markAllPresent}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition shadow-sm flex items-center gap-2"
          >
            <CheckCircle className="w-4 h-4" /> Mark All Present
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* ─── Stats Cards ────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <p className="text-xs text-slate-500 font-medium">Total Employees</p>
          <p className="text-2xl font-bold text-slate-800">{employees.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 border-l-4 border-l-green-500">
          <p className="text-xs text-slate-500 font-medium">✅ Present</p>
          <p className="text-2xl font-bold text-green-600">{totalPresent}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 border-l-4 border-l-red-500">
          <p className="text-xs text-slate-500 font-medium">❌ Absent</p>
          <p className="text-2xl font-bold text-red-600">{totalAbsent}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 border-l-4 border-l-amber-500">
          <p className="text-xs text-slate-500 font-medium">⏳ Not Marked</p>
          <p className="text-2xl font-bold text-amber-600">{totalNotMarked}</p>
        </div>
      </div>

      {/* ─── Filters ────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-slate-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
            >
              <option value="all">All Status</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>{statusConfig[s].label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ─── Attendance Table ──────────────────────────────────── */}
      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Check In
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                      No employees found matching your filters.
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((employee) => {
                    const record = attendance.find((a) => a.employee === employee.id);
                    const currentStatus = record?.status || 'not_marked';
                    const isMarked = record !== undefined;

                    return (
                      <tr key={employee.id} className="hover:bg-slate-50 transition">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-semibold">
                              {employee.name?.charAt(0) || 'U'}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-800">{employee.name}</p>
                              <p className="text-xs text-slate-500">{employee.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {employee.department || '—'}
                        </td>
                        <td className="px-4 py-3">
                          {isMarked ? (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig[currentStatus]?.color || 'bg-slate-100 text-slate-600'}`}>
                              {statusConfig[currentStatus]?.label || currentStatus}
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-600">
                              Not Marked
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {record?.checkIn || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {statusOptions.map((status) => {
                              const Icon = statusConfig[status].icon;
                              const isActive = currentStatus === status;
                              return (
                                <button
                                  key={status}
                                  onClick={() => markAttendance(employee.id, status)}
                                  className={`p-1.5 rounded transition ${
                                    isActive
                                      ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-400'
                                      : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
                                  }`}
                                  title={`Mark as ${statusConfig[status].label}`}
                                >
                                  <Icon className="w-5 h-5" />
                                </button>
                              );
                            })}
                            {isMarked && (
                              <button
                                onClick={() => markAttendance(employee.id, '')}
                                className="p-1.5 text-red-400 hover:bg-red-50 rounded transition"
                                title="Clear status"
                              >
                                <XCircle className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;